import type { TextFontSizeModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import { FONT_SIZE_OPTIONS } from "@v2/features/modifiers/constants";
import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import { ChevronDown } from "lucide-react";
import { parseUnit } from "replo-runtime/shared/utils/units";

/** Lowercase values - parseUnit does case-insensitive matching */
const ALLOWED_NON_UNIT_VALUES = ["auto"];

interface FontSizeModifierProps {
  onChange: (modifiers: TextFontSizeModifierValue[]) => void;
}

const FontSizeModifier: React.FC<FontSizeModifierProps> = ({ onChange }) => {
  const currentValue =
    useOptimisticModifierValue({
      modifierType: { type: "fontSize", value: 0 },
      extractor: (elementDesignation) => {
        return elementDesignation.element.styleOverrides?.fontSize ?? null;
      },
    }) ?? null;

  // NOTE (Kevin, 2025-12-10): When currentValue is null, display "Auto" to indicate inherited font size.
  const displayValue = currentValue != null ? `${currentValue}px` : "Auto";

  const menuOptions = FONT_SIZE_OPTIONS.map((option) => ({
    label: option.label,
    value: option.value,
  }));

  const handleChange = (newValue: string | null) => {
    /** NOTE (Kevin, 2025-12-10): Handle "Auto" selection - null or "auto" (case-insensitive) means removal */
    if (newValue === null || newValue.toLowerCase() === "auto") {
      onChange([{ type: "fontSize" as const, value: null }]);
      return;
    }

    const result = parseUnit(
      newValue,
      { value: 0, unit: "px" },
      "fontSize",
      "px",
    );

    onChange([
      {
        type: "fontSize" as const,
        value: Number(result.value),
      },
    ]);
  };

  return (
    <div className="font-size-modifier">
      <LengthInputSelector
        field="fontSize"
        value={displayValue}
        onChange={handleChange}
        placeholder="1px"
        metrics={["px"]}
        unitDefaults={{ value: "1", unit: "px" }}
        menuOptions={menuOptions}
        minValues={{ px: 1 }}
        inputClassName="w-full"
        endEnhancer={() => <ChevronDown size={12} />}
        allowedNonUnitValues={ALLOWED_NON_UNIT_VALUES}
        transformValue={(value: string) => {
          return value.toLowerCase() === "auto" ? "Auto" : value;
        }}
      />
    </div>
  );
};

export default FontSizeModifier;
